import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Logo from 'svg/img/logo.png';
import Grid from '@mui/material/Grid';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { portfolioLocations } from 'data/portfolioLocations';

const Topbar = ({
  onSidebarOpen,
}) => {
  const closeTimerRef = useRef(null);
  const portfolioTriggerRef = useRef(null);
  const portfolioMenuRef = useRef(null);
  const [isPortfolioMenuOpen, setIsPortfolioMenuOpen] = useState(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handlePortfolioOpen = () => {
    clearCloseTimer();
    setIsPortfolioMenuOpen(true);
  };

  const handlePortfolioClose = () => {
    clearCloseTimer();
    setIsPortfolioMenuOpen(false);
  };

  const schedulePortfolioClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsPortfolioMenuOpen(false);
      closeTimerRef.current = null;
    }, 160);
  };

  const handlePortfolioMouseLeave = (event) => {
    const nextTarget = event.relatedTarget;

    if (
      nextTarget instanceof Node
      && (
        portfolioTriggerRef.current?.contains(nextTarget)
        || portfolioMenuRef.current?.contains(nextTarget)
      )
    ) {
      clearCloseTimer();
      return;
    }

    schedulePortfolioClose();
  };

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <Grid container spacing={4}
      display={'flex'}
      alignItems={'center'}
      width={'100%'}

    >
      {/** This grid element holds the logo and other stuff, it appear above the navigation links */}
      <Grid item container alignItems={'center'} justifyContent={'space-between'} xs={12} md={12}>
        {/** Hamburger menu */}
        <Box display={'flex'} alignItems={'center'}>
          <Box marginRight={{ xs: 1, sm: 2 }} sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton onClick={onSidebarOpen} aria-label="Menu">
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
        {/** Logo */}
        <Box
          display={'flex'}
          alignItems="baseline"
          component="a"
          underline="none"
          href="/"
          title="bcb"
          width={'40%'}
          height={'undefined'}
          aspectRatio={1}
          zIndex={1}
        >
          <Box ><img style={{ maxWidth: '100%' }} src={Logo} alt="BCB Logo" /></Box>
        </Box>
        {/** Dark mode toggler */}
        <Box display="flex" alignItems={'center'}>

        </Box>
      </Grid>
      {/** This Grid element makes the bottons appear below the logo */}
      <Grid item container sx={{ display: { xs: 'none', md: 'flex' } }} justifyContent={'center'} alignItems={'center'} xs={12} md={12} marginBottom={4}>
        <Box marginX={4}>
          <Link
            underline="none"
            component="a"
            href="/"
            color="textPrimary"
            sx={{
              fontWeight: 500,
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            Home
          </Link>
        </Box>
        <Box marginX={4}>
          <Box
            ref={portfolioTriggerRef}
            onMouseEnter={handlePortfolioOpen}
            onMouseLeave={handlePortfolioMouseLeave}
          >
            <Link
              underline="none"
              component="a"
              href="/portfolio"
              onMouseEnter={handlePortfolioOpen}
              color="textPrimary"
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              Portfolio
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  transform: isPortfolioMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Link>
            <Popper
              anchorEl={portfolioTriggerRef.current}
              open={isPortfolioMenuOpen}
              placement="bottom"
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 6],
                  },
                },
              ]}
              sx={{ zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={handlePortfolioClose}>
                <Paper
                  ref={portfolioMenuRef}
                  onMouseEnter={clearCloseTimer}
                  onMouseLeave={handlePortfolioMouseLeave}
                  sx={{
                    minWidth: 280,
                    borderRadius: 2,
                    paddingY: 1,
                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.14)',
                  }}
                >
                  <MenuList autoFocusItem={false}>
                    {portfolioLocations.map((location) => (
                      <MenuItem
                        key={location.href}
                        component={'a'}
                        href={location.href}
                        onClick={handlePortfolioClose}
                        sx={{
                          alignItems: 'flex-start',
                          paddingX: 2,
                          paddingY: 1.5,
                        }}
                      >
                        <Box>
                          <Typography variant={'overline'} color={'primary.main'} display={'block'}>
                            {location.eyebrow}
                          </Typography>
                          <Typography variant={'subtitle2'} color={'textPrimary'}>
                            {location.title}
                          </Typography>
                          <Typography variant={'body2'} color={'textSecondary'}>
                            {location.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Box>
        <Box marginX={4}>
          <Link
            underline="none"
            component="a"
            href="/about"
            color="textPrimary"
            sx={{
              fontWeight: 500,
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            About Us
          </Link>
        </Box>
        <Box marginX={4}>
          <Link
            underline="none"
            component="a"
            href="/contact"
            color="textPrimary"
            sx={{
              fontWeight: 500,
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            Contact Us
          </Link>
        </Box>
      </Grid>
    </Grid>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func,
  themeToggler: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  setThemePalette: PropTypes.func.isRequired,
  paletteType: PropTypes.string.isRequired,
};

export default Topbar;
